import { supplierPartNumber } from './storeListings.js';
/** Per-unit shipping rate in cents by class. Rates are per unit and summed
 *  across every line; there is NO unit cap. */
export const SHIPPING_RATE_CENTS = {
    elite_home: 1500,
    elite_commercial: 2500,
    ac_pro: 3500,
    ultra_single: 14900,
    ultra_dual: 19900,
    pedestal: 29900,
    dc: 49900,
    free: 0,
};
/** At or above this many charger units a cart is flagged "bulk": still fully
 *  checkout-eligible, but the placed order is flagged for a freight confirm and
 *  a one-business-day buyer follow-up. No longer a checkout block. */
export const BULK_QUOTE_MIN_CHARGER_UNITS = 4;
/** Afterpay's US ceiling: at or under this a buyer may be offered Afterpay;
 *  above it only Affirm can finance the purchase. */
export const AFTERPAY_MAX_CENTS = 200000;
/** Known pedestal-freight SKUs by part number. The composite AC Ultra pedestal
 *  full set ships LTL freight; add future pedestal full-set SKUs here (or rely
 *  on the commercial_ac + 'pedestal' name rule). */
const PEDESTAL_FREIGHT_PARTS = new Set(['MU38FN7N-CMSA2']);
/** Elite Home SKUs. Matched by part number as well as tier 'home' so a cart
 *  item persisted before the tier snapshot still classifies correctly. */
const ELITE_HOME_PARTS = new Set(['MC40AP6I', 'MC40AP14I', 'MC50AHI']);
/** Per-class charger SKUs matched by exact part number. New chargers are added
 *  here, the same explicit-SKU pattern as PEDESTAL_FREIGHT_PARTS. A second
 *  listing of the same hardware (storeListings.ts) is looked up by its Autel
 *  part, so it ships in the same box at the same rate as the original. */
const CLASS_BY_PART = {
    MCC50AHI: 'elite_commercial',
    UW19C002: 'ac_pro',
    UF19L001: 'ultra_single',
    UF38L001: 'ultra_dual',
};
/**
 * A pedestal FREIGHT unit: the AC Ultra pedestal charging full sets that ship
 * as oversize LTL freight, not the small pedestal-post accessories. Matched by
 * the known SKU OR any commercial_ac product whose name reads as a pedestal.
 * Deliberately EXCLUDES the pedestal-post accessories (tier 'accessory' ->
 * free), which ship free with the rest of the accessories.
 */
export function isPedestalFreight(p) {
    if (PEDESTAL_FREIGHT_PARTS.has(p.part_number))
        return true;
    return p.tier === 'commercial_ac' && /pedestal/i.test(p.name);
}
/**
 * Classify one product into its shipping class. Order matters: pedestal freight
 * first (so a commercial_ac full set is never mistaken for another class), then
 * the exact per-SKU charger classes, then Elite Home (by SKU or tier 'home'),
 * then free (accessories, RFID, UP10, and anything unlisted).
 */
export function shippingClassFor(p) {
    if (isPedestalFreight(p))
        return 'pedestal';
    const byPart = CLASS_BY_PART[supplierPartNumber(p.part_number)];
    if (byPart)
        return byPart;
    if (p.tier === 'home' || ELITE_HOME_PARTS.has(p.part_number))
        return 'elite_home';
    // DC fast chargers (store slice 5) ship LTL freight at a flat per-unit rate.
    if (p.tier === 'dc')
        return 'dc';
    return 'free';
}
/** True when the item ships free and does NOT count toward the charger-unit
 *  total (accessories, RFID, UP10, anything unlisted). */
export function isFreeShipping(p) {
    return shippingClassFor(p) === 'free';
}
/**
 * The one-line, per-unit shipping label for a class, derived from the rate so
 * the copy and the charge never drift. Pedestal reads as freight per unit; the
 * other charger classes read "$N shipping"; free items read "Free shipping".
 */
export function shippingUnitLabel(cls) {
    const cents = SHIPPING_RATE_CENTS[cls];
    if (cents === 0)
        return 'Free shipping';
    const dollars = Math.round(cents / 100);
    // DC freight is an ESTIMATE confirmed on order (Warren's shipping policy);
    // the label says so wherever a DC freight line renders.
    if (cls === 'dc')
        return `$${dollars} freight per unit (estimate, confirmed on order)`;
    return cls === 'pedestal' ? `$${dollars} freight per unit` : `$${dollars} shipping`;
}
/**
 * Count charger units in a cart: the sum of quantities of every non-free item.
 * Accessories, RFID, and UP10 (free) are excluded, per policy. Drives the
 * bulk-quote gate.
 */
export function countChargerUnits(items) {
    let n = 0;
    for (const item of items) {
        if (shippingClassFor(item) !== 'free')
            n += item.quantity;
    }
    return n;
}
/** True when the cart is "bulk" (4 or more charger units). Advisory only under
 *  the any-quantity policy: callers use it to flag the order and surface bulk
 *  messaging, never to block checkout. */
export function requiresBulkQuote(items) {
    return countChargerUnits(items) >= BULK_QUOTE_MIN_CHARGER_UNITS;
}
/**
 * Freight for a VALIDATED cart: the sum of every item's per-unit rate times its
 * quantity, across all classes, with NO unit cap. Returns null when everything
 * ships free (the caller then attaches no shipping option, so no shipping line
 * appears and shipping_cents lands 0). There is no unit gate: every cart is
 * charged its summed per-unit freight, and 4+ charger carts are merely flagged
 * bulk downstream.
 */
export function computeStoreFreight(items) {
    let total = 0;
    let hasFreight = false;
    let hasShipping = false;
    for (const item of items) {
        const cls = shippingClassFor(item);
        const rate = SHIPPING_RATE_CENTS[cls];
        if (rate <= 0)
            continue;
        total += rate * item.quantity;
        if (cls === 'pedestal' || cls === 'dc')
            hasFreight = true;
        else
            hasShipping = true;
    }
    if (total === 0)
        return null;
    const label = hasFreight && hasShipping
        ? 'Shipping and freight'
        : hasFreight
            ? 'Freight and handling'
            : 'Shipping';
    return { amount_cents: total, label };
}
/** Which installment providers a buy-now item's price qualifies for. 'both'
 *  means Afterpay may be named; 'affirm' means Affirm only. */
export function installmentProvidersFor(priceCents) {
    return priceCents <= AFTERPAY_MAX_CENTS ? 'both' : 'affirm';
}
//# sourceMappingURL=storeShipping.js.map